import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { apolloClient } from 'lib/withApollo'
import getSitesForStaticPaths from 'helpers/getSitesForStaticPaths'
import BOARD_QUERY from 'graphql/board.query'
import BOARD_MENU_QUERY from 'graphql/boardMenu.query'
import BOARD_PARENT_QUERY from 'graphql/boardParent.query'
import MODELS_QUERY from 'graphql/models.query'

import Layout from 'components/Layout'
import WordpressSEO from 'components/WordpressSEO'
import SliderCover from 'components/SliderCover'
import ModelList from 'components/ModelList'
import BoardsMenu from 'components/BoardsMenu'
import BoardsAlphabeticalFilter from 'components/BoardsAlphabeticalFilter'

import s from './board.module.css'

function Board({
  title,
  seo,
  acfModelBoardsCover,
  boards,
  boardUri,
  boardType,
  boardSlug,
  site,
  globalSettings,
  models,
}) {
  const covers = acfModelBoardsCover?.highlightOrder || null

  const boardUrl = `${boardType}${boardSlug ? `/${boardSlug}` : ''}`
  const [boardToggled, setBoardToggled] = useState(false)
  const [showFollowers, setShowFollowers] = useState(false)
  const [inTownOnly, setInTownOnly] = useState(false)
  const [activeFilter, setActiveFilter] = useState(null)

  const handleBoardToggle = () => {
    setBoardToggled(!boardToggled)
  }

  const handleFollowersToggle = () => {
    setShowFollowers(!showFollowers)
  }

  useEffect(() => {
    setActiveFilter(null)
  }, [boardUrl])

  const setBoardsMenu = (classname = '') => {
    return (
      <BoardsMenu
        className={classname}
        currentBoard={boardSlug}
        boards={boards}
        site={site}
        boardType={boardType}
        boardToggled={boardToggled}
        handleBoardToggle={handleBoardToggle}
        inTownToggled={inTownOnly}
        toggleInTown={() => setInTownOnly(!inTownOnly)}
        handleFollowersToggle={handleFollowersToggle}
      />
    )
  }

  return (
    <Layout globalSettings={globalSettings}>
      <WordpressSEO
        url={`/${site}/${boardType}/${boardSlug}`}
        seo={seo}
        title={title}
      />
      <div
        className={classNames(s.container, {
          [s.containerTopSpacing]: !covers,
        })}
      >
        {setBoardsMenu(s.boardsMenuMobile)}
        {covers && <SliderCover className={s.slider} covers={covers} />}
        {setBoardsMenu(s.boardsMenu)}
        <ModelList
          group={boardType}
          boardSlug={boardSlug}
          models={
            activeFilter || inTownOnly
              ? models.filter((m) => {
                  if (inTownOnly && !m.inTown) return false
                  if (!activeFilter) return true
                  return m.firstName.charAt(0).toUpperCase() === activeFilter
                })
              : models
          }
          showFollowers={showFollowers}
        />
        {models.length > 0 && (
          <BoardsAlphabeticalFilter
            models={models}
            activeFilter={activeFilter}
            setFilter={(f) => setActiveFilter(f)}
          />
        )}
      </div>
    </Layout>
  )
}

Board.propTypes = {
  seo: PropTypes.object,
  boards: PropTypes.array,
  boardUri: PropTypes.string,
  boardType: PropTypes.string,
  boardSlug: PropTypes.string,
  site: PropTypes.string,
  globalSettings: PropTypes.object,
}
Board.defaultProps = {
  seo: {},
  boards: [],
  boardUri: '',
  boardType: '',
  boardSlug: '',
  site: '',
  globalSettings: {},
}

export default Board

export async function getStaticProps({ params }) {
  const { site, boardType, boardSlug } = params

  const { data: parentData, error: parentError } = await apolloClient().query({
    query: BOARD_PARENT_QUERY,
    context: { site },
    variables: { boardUri: boardType },
  })

  if (parentError)
    console.log(`Error loading Board parent props - ${parentError}`)

  const { parent } = parentData

  const boards = parent?.acfModelBoardParent?.departmentOrder || null
  const boardUri =
    boardSlug || parent?.acfModelBoardParent?.departmentOrder?.[0]?.slug

  const { data, error } = await apolloClient().query({
    query: BOARD_QUERY,
    context: { site },
    variables: { boardUri: `${boardType}/${boardUri}` },
  })

  if (error) console.log(`Error loading Board props - ${error}`)
  if (!data) return null

  const { data: solarnetData, error: solarnetError } =
    await apolloClient().query({
      query: MODELS_QUERY,
      context: { site: 'solarnet' },
      variables: { boardType, boardUri, site },
    })

  if (solarnetError)
    console.log(`Error loading Board Solarnet props - ${solarnetError}`)
  if (!solarnetData) return null

  return {
    props: {
      ...params,
      ...data.board,
      ...solarnetData,
      boards,
    },
    revalidate: 60 * 2,
  }
}

export async function getStaticPaths() {
  const paths = []

  await getSitesForStaticPaths(
    ({ site }) => ({
      query: BOARD_MENU_QUERY,
      context: { site },
    }),
    (data, { site }) =>
      data.modelBoards.nodes.forEach((board) =>
        board.acfModelBoardParent.departmentOrder.map((b) =>
          paths.push({
            params: {
              site,
              boardType: board.slug,
              boardSlug: b.slug,
            },
          })
        )
      )
  )

  return {
    paths,
    fallback: 'blocking',
  }
}
